// Vignettes de niveaux (chantier A3, 16/07) : mini-rendu du TERRAIN d'une carte dans un
// canvas offscreen, mis en cache par clé (slug+updatedAt) et blitté par le sélecteur.
// Cosmétique PUR, hors simulation : on parse la grille via levelFromAscii (source de vérité,
// caisses/spawns/sorties compris) et on ne dessine que l'essentiel lisible à ~6 px/case
// (terrain, DÉCORS, or, sorties, caisses, moines, départ J1). Grille invalide -> null (cadre neutre).
import { BIG_DECOS, bigOfCode, decorGrid, kindOfCode } from '../shared/decor'
import { levelFromAscii } from '../shared/level'
import { T, colOf, rowOf } from '../shared/types'
import type { WorldKey } from '../shared/worlds'
import { drawBigDeco, worldBackground } from './render'
import { blitTuileMini, blitVignette } from './vignette-papier'
import {
  CRATE_METAL, CRATE_WOOD, DECO_SPRITES, GOLD, MONK_FRAMES, RUNNER_FRAMES, TILE_SPRITES, WORLD_TILES,
  type SpriteRef,
} from './sprites'

const S = 6 // px par case : 28x16 -> 168x96 ; 56x32 -> 336x192 (réduit au blit letterboxé)

const cache = new Map<string, HTMLCanvasElement | null>()

/** Vignette (canvas offscreen) d'une carte, ou null si la grille est invalide. Cachée par clé.
 *  `decor` (grille ASCII des décors, MÊME largeur que le terrain — contrat éditeur) : dessine
 *  aussi les décors, petits (spritesheet) et grands (decors.png, figés t=0). Vide -> aucun décor
 *  (rétrocompatible : un appelant qui ne connaît pas la couche déco garde le rendu d'avant). */
export function levelThumb(sheet: HTMLImageElement, key: string, rows: string[], world: WorldKey, decor = ''): HTMLCanvasElement | null {
  const hit = cache.get(key)
  if (hit !== undefined) return hit
  let cv: HTMLCanvasElement | null = null
  try {
    const lvl = levelFromAscii(key, rows)
    cv = document.createElement('canvas')
    cv.width = lvl.cols * S
    cv.height = lvl.rows * S
    const g = cv.getContext('2d')!
    // FOND du biome (19/07 : « sur les miniatures on doit voir les fonds d'écran ») : worldBackground
    // est le ciel/décor vectoriel du monde (1344x768, même ratio 1.75 que la vignette) ; on le réduit
    // LISSÉ pour un dégradé propre, puis on repasse en NET pour le terrain/décors en pixel art.
    g.imageSmoothingEnabled = true
    g.drawImage(worldBackground(world), 0, 0, 1344, 768, 0, 0, cv.width, cv.height)
    g.imageSmoothingEnabled = false
    const wt = WORLD_TILES[world]
    const blit = (ref: SpriteRef, cell: number, cols = lvl.cols): void => {
      g.drawImage(sheet, ref.sx, ref.sy, 16, 16, colOf(cell, cols) * S, rowOf(cell, cols) * S, S, S)
    }
    for (let cell = 0; cell < lvl.terrain.length; cell++) {
      const t = lvl.terrain[cell] ?? T.Empty
      if (t === T.Empty) continue
      // REFONTE (29/07) : la miniature prend les couleurs du PAPIER, comme la carte
      // qu'elle annonce. Un char sans dessin papier retombe sur l'ancien sprite.
      if (blitTuileMini(g, t, world, colOf(cell, lvl.cols) * S, rowOf(cell, lvl.cols) * S, S)) continue
      // Brique/fausse brique/béton : variante du BIOME (comme le rendu du jeu) ; le reste, sprite commun.
      const ref = t === T.Brick || t === T.FalseBrick ? wt.brick : t === T.Concrete ? wt.concrete : TILE_SPRITES[t]
      if (ref) blit(ref, cell)
    }
    // DÉCORS (19/07, demande Claude-Alain « les déco doivent être visibles sur les miniatures ») :
    // par-dessus le terrain, sous les entités. La grille déco a la même largeur que le terrain ; on
    // la déduit de la chaîne (auto-cohérent). Grands décors D'ABORD (fond), petits par-dessus.
    if (decor) {
      const dg = decorGrid(decor)
      const dCols = decor.split('\n')[0]?.length || lvl.cols
      for (let cell = 0; cell < dg.length; cell++) {
        const big = bigOfCode(dg[cell] ?? 0)
        if (!big) continue
        const meta = BIG_DECOS[big]
        drawBigDeco(g, big, (colOf(cell, dCols) - meta.ax) * S, (rowOf(cell, dCols) - meta.ay) * S, 0, S)
      }
      for (let cell = 0; cell < dg.length; cell++) {
        const k = kindOfCode(dg[cell] ?? 0)
        const dref = k ? DECO_SPRITES[k] : undefined
        if (dref) blit(dref, cell, dCols)
      }
    }
    // Ce qui n'est pas du terrain (sortie, or, caisses, moines, départ) passe par le
    // même pont papier, avec repli sur l'ancien sprite. La sortie 'E' est hors
    // terrain (cachée au départ), d'où sa liste à part.
    const mini = (ch: string, ref: SpriteRef, cell: number): void => {
      if (!blitVignette(g, ch, world, colOf(cell, lvl.cols) * S, rowOf(cell, lvl.cols) * S, S)) blit(ref, cell)
    }
    for (const cell of lvl.exitCells) mini('E', TILE_SPRITES[T.ExitLadder]!, cell)
    for (const cell of lvl.golds) mini('$', GOLD, cell)
    for (const cr of lvl.crates) mini(cr.kind === 'metal' ? 'X' : 'F', cr.kind === 'metal' ? CRATE_METAL : CRATE_WOOD, cr.cell)
    for (const m of lvl.monkSpawns) mini('m', MONK_FRAMES.run[0], m.cell)
    if (lvl.spawnP1 >= 0) mini('1', RUNNER_FRAMES.idle, lvl.spawnP1)
  } catch {
    cv = null // grille invalide (carte inachevée) : le sélecteur affiche un cadre neutre « ? »
  }
  cache.set(key, cv)
  return cv
}
